import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Activity, Cable, CheckCircle2, Power, RefreshCw,
  ShieldAlert, Loader2, Play, Radio, Clock, WifiOff,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';

interface Waveshare8CHDashboardProps {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
}

const CHANNELS = Array.from({ length: 8 }, (_, i) => i + 1);

// Inputs and outputs keep their existing identity colors across every element.
const DI_COLOR = '#10b981'; // emerald
const DO_COLOR = '#06b6d4'; // cyan
const TIMELINE_WINDOW_MS = 60_000;
const HISTORY_KEEP_MS = 5 * 60_000;
const CONFIRM_TIMEOUT_MS = 15_000;

type Transition = { t: number; v: boolean };

const ago = (ts?: number) => {
  if (!ts) return 'no change yet';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 2) return 'changed just now';
  if (s < 60) return `changed ${s}s ago`;
  if (s < 3600) return `changed ${Math.floor(s / 60)}m ago`;
  return `changed ${new Date(ts).toLocaleTimeString()}`;
};

// ──────────────────────────────────────────────────
// Animated pieces
// ──────────────────────────────────────────────────

function CountUp({ value }: { value: number }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={value}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -12, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 420, damping: 30 }}
        className="inline-block tabular-nums"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
}

/** 8 small segments, lit per active channel — a glanceable bank state. */
function SegmentBar({ states, color }: { states: boolean[]; color: string }) {
  return (
    <div className="mt-3 flex gap-1" aria-hidden>
      {states.map((on, i) => (
        <motion.span
          key={i}
          className="h-1.5 flex-1 rounded-full"
          animate={{ backgroundColor: on ? color : 'rgba(127,127,127,0.18)', boxShadow: on ? `0 0 8px ${color}99` : '0 0 0 transparent' }}
          transition={{ duration: 0.25 }}
        />
      ))}
    </div>
  );
}

function Led({ on, color, size = 12, label }: { on: boolean; color: string; size?: number; label?: string }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }} aria-label={label}>
      <motion.span
        className="absolute inset-0 rounded-full"
        animate={{
          backgroundColor: on ? color : 'rgba(127,127,127,0.28)',
          boxShadow: on ? `0 0 ${size}px ${color}, 0 0 ${size * 2}px ${color}66` : '0 0 0 transparent',
        }}
        transition={{ duration: 0.2 }}
      />
      {on && <span className="absolute inset-[22%] rounded-full bg-white/70" />}
    </span>
  );
}

/** Drawing of the 8CH module: two rows of channel LEDs plus PWR / RX status LEDs. */
function ModuleHero({
  di, doo, connected, packetAt, name,
}: { di: boolean[]; doo: boolean[]; connected: boolean; packetAt?: number; name: string }) {
  const reduce = useReducedMotion();
  return (
    <div className="relative overflow-hidden rounded-xl border bg-gradient-to-b from-muted/60 to-muted/20 p-4">
      {/* DIN rail */}
      <div className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 bg-foreground/5" aria-hidden />
      <div className="relative mx-auto max-w-xl rounded-lg border-2 border-foreground/15 bg-card p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Modbus RTU IO 8CH</div>
          <div className="flex items-center gap-3 text-[10px] font-semibold text-muted-foreground">
            <span className="flex items-center gap-1.5"><Led on={connected} color={DI_COLOR} size={8} label="Power" /> PWR</span>
            <span className="flex items-center gap-1.5">
              <span className="relative inline-flex h-2 w-2">
                {packetAt && !reduce ? (
                  <motion.span key={packetAt} className="absolute inset-0 rounded-full" initial={{ backgroundColor: '#fab219', boxShadow: '0 0 10px #fab219' }} animate={{ backgroundColor: 'rgba(127,127,127,0.28)', boxShadow: '0 0 0 transparent' }} transition={{ duration: 0.7 }} />
                ) : (
                  <span className="absolute inset-0 rounded-full" style={{ backgroundColor: 'rgba(127,127,127,0.28)' }} />
                )}
              </span>
              RX
            </span>
          </div>
        </div>
        {[{ label: 'DI', states: di, color: DI_COLOR }, { label: 'DO', states: doo, color: DO_COLOR }].map((row) => (
          <div key={row.label} className="mb-2 flex items-center gap-3 last:mb-0">
            <span className="w-6 text-[10px] font-bold text-muted-foreground">{row.label}</span>
            <div className="grid flex-1 grid-cols-8 gap-2">
              {row.states.map((on, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <Led on={on} color={row.color} size={14} label={`${row.label}${i + 1} ${on ? 'on' : 'off'}`} />
                  <span className="text-[9px] tabular-nums text-muted-foreground">{i + 1}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {/* terminal blocks */}
        <div className="mt-3 grid grid-cols-8 gap-2 pl-9" aria-hidden>
          {CHANNELS.map((ch) => (
            <div key={ch} className="h-2.5 rounded-sm border border-foreground/15 bg-foreground/10" />
          ))}
        </div>
      </div>
      <div className="relative mt-2 text-center text-xs text-muted-foreground">{name}</div>
    </div>
  );
}

/** Logic-analyzer row: filled where the signal was ON over the last 60 s. */
function TimelineRow({
  label, color, history, current, now,
}: { label: string; color: string; history: Transition[]; current: boolean | null; now: number }) {
  const start = now - TIMELINE_WINDOW_MS;
  const W = 600;
  const segments: { x: number; w: number }[] = [];
  if (current !== null) {
    // history[0] is the first sample seen (initial state, not a switch); nothing is
    // known before it, so nothing is drawn there.
    const before = [...history].reverse().find((h) => h.t < start);
    const relevant = history.filter((h) => h.t >= start);
    let state = before ? before.v : current;
    let cursor = start;
    let from = 0;
    if (!before && relevant.length) {
      cursor = relevant[0].t;
      state = relevant[0].v;
      from = 1;
    }
    for (const h of relevant.slice(from)) {
      if (state) segments.push({ x: ((cursor - start) / TIMELINE_WINDOW_MS) * W, w: ((h.t - cursor) / TIMELINE_WINDOW_MS) * W });
      cursor = h.t;
      state = h.v;
    }
    if (state) segments.push({ x: ((cursor - start) / TIMELINE_WINDOW_MS) * W, w: ((now - cursor) / TIMELINE_WINDOW_MS) * W });
  }
  const edges = history.slice(1).filter((h) => h.t >= start).map((h) => ((h.t - start) / TIMELINE_WINDOW_MS) * W);
  return (
    <div className="flex items-center gap-3">
      <span className="w-10 shrink-0 text-xs font-semibold tabular-nums">{label}</span>
      <svg viewBox={`0 0 ${W} 16`} preserveAspectRatio="none" className="h-4 flex-1" aria-label={`${label} signal, last 60 seconds`}>
        <line x1={0} y1={13} x2={W} y2={13} stroke="currentColor" strokeOpacity={0.2} vectorEffect="non-scaling-stroke" className="text-foreground" />
        {segments.map((s, i) => (
          <rect key={i} x={s.x} y={3} width={Math.max(0.5, s.w)} height={10} fill={color} opacity={0.85} rx={1} />
        ))}
        {edges.map((x, i) => (
          <line key={`e${i}`} x1={x} y1={1} x2={x} y2={15} stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <span className="w-8 shrink-0 text-right text-[10px] font-semibold" style={{ color: current ? color : undefined }}>
        {current === null ? '—' : current ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

/** Time axis aligned with the TimelineRow plot area (same label/value gutters). */
function TimelineAxis() {
  return (
    <div className="flex items-center gap-3 pt-1 text-[10px] text-muted-foreground">
      <span className="w-10 shrink-0" />
      <div className="flex flex-1 justify-between"><span>−60s</span><span>−30s</span><span>now</span></div>
      <span className="w-8 shrink-0" />
    </div>
  );
}

/** Animated switch for an output; the knob slides and the track fills. */
function OutputSwitch({ on, busy, onClick, label }: { on: boolean; busy: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={onClick}
      className="relative h-8 w-16 shrink-0 rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait"
      style={{ backgroundColor: on ? `${DO_COLOR}33` : 'rgba(127,127,127,0.15)', borderColor: on ? `${DO_COLOR}88` : 'rgba(127,127,127,0.3)' }}
    >
      <motion.span
        className="absolute top-1 grid h-6 w-6 place-items-center rounded-full shadow-md"
        animate={{ left: on ? 34 : 4, backgroundColor: on ? DO_COLOR : '#94a3b8' }}
        transition={{ type: 'spring', stiffness: 500, damping: 32 }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-white" /> : <Power className="h-3.5 w-3.5 text-white" />}
      </motion.span>
    </button>
  );
}

export function Waveshare8CHDashboard({
  clientId,
  token,
  pollingIntervalMs = 5000,
}: Waveshare8CHDashboardProps) {
  const reduce = useReducedMotion();
  const [scanners, setScanners] = useState<any[]>([]);
  const [selectedScannerId, setSelectedScannerId] = useState<string | null>(null);
  const [liveReadings, setLiveReadings] = useState<any[]>([]);
  const [scannerLastSeen, setScannerLastSeen] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [togglingChannel, setTogglingChannel] = useState<number | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [lastPacketAt, setLastPacketAt] = useState<number | undefined>(undefined);
  // Output writes awaiting confirmation from the device's own reported state.
  const [pendingTargets, setPendingTargets] = useState<Record<number, { value: boolean; at: number }>>({});
  const [bulk, setBulk] = useState<{ state: boolean; done: number; total: number } | null>(null);
  const historyRef = useRef<Record<string, Transition[]>>({});
  const [, forceStatusTick] = useState(0);

  // Re-render every second: keeps the Connected badge, "changed Xs ago" and the
  // scrolling signal timeline accurate between packets.
  useEffect(() => {
    const id = setInterval(() => forceStatusTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ──────────────────────────────────────────────────
  // Load scanners from backend
  // ──────────────────────────────────────────────────
  const loadScanners = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/scanners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load scanners');
      const data = await res.json();
      const list: any[] = data.scanners || [];
      setScanners(list);
      setSelectedScannerId((prev) => {
        if (prev && list.some((s) => s.id === prev)) return prev;
        const ws = list.find((s) => s.name?.toLowerCase().includes('waveshare'));
        return ws?.id ?? list[0]?.id ?? null;
      });
    } catch {
      toast.error('Failed to load scanners');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => { loadScanners(); }, [loadScanners]);

  // ──────────────────────────────────────────────────
  // WebSocket live data
  // ──────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });

    const handleLive = (data: any) => {
      if (!mounted) return;
      if (data.type !== 'plc_live_data' || data.connectionType !== 'scanner') return;

      if (data.plcId) {
        setScannerLastSeen((prev) => ({ ...prev, [data.plcId]: Date.now() }));
      }
      if (selectedScannerId && data.plcId !== selectedScannerId) return;

      const now = Date.now();
      setLastPacketAt(now);
      // Record edges for the signal timeline / "changed Xs ago".
      for (const [name, value] of Object.entries(data.values ?? {})) {
        const v = Boolean(value);
        const hist = historyRef.current[name] || [];
        const last = hist[hist.length - 1];
        if (!last || last.v !== v) {
          hist.push({ t: now, v });
          historyRef.current[name] = hist.filter((h) => h.t >= now - HISTORY_KEEP_MS);
        }
      }

      setLiveReadings((prev) => {
        const next = [...prev];
        for (const [name, value] of Object.entries(data.values ?? {})) {
          const idx = next.findIndex((r) => r.tag_name === name);
          if (idx !== -1) next[idx] = { ...next[idx], value };
          else next.push({ tag_name: name, value });
        }
        return next;
      });
    };

    ws.on('plc_live_data', handleLive);
    ws.connect();
    return () => {
      mounted = false;
      ws.off('plc_live_data', handleLive);
    };
  }, [clientId, token, pollingIntervalMs, selectedScannerId]);

  // Reset live readings when user switches scanner device
  useEffect(() => {
    setLiveReadings([]);
    setPendingTargets({});
    setLastPacketAt(undefined);
    historyRef.current = {};
  }, [selectedScannerId]);

  // ──────────────────────────────────────────────────
  // Derived state
  // ──────────────────────────────────────────────────
  const ONLINE_WINDOW = Math.max(30_000, pollingIntervalMs * 3);
  const currentScanner = scanners.find((s) => s.id === selectedScannerId) ?? null;
  const connected = currentScanner
    ? Date.now() - (scannerLastSeen[currentScanner.id] ?? 0) < ONLINE_WINDOW
    : false;

  // DI tags: inputType 102 (FC02 Discrete Inputs)
  const diTags = useMemo(
    () => (currentScanner?.tags ?? []).filter((t: any) => t.inputType === 102 || t.input_type === 102),
    [currentScanner],
  );
  // DO tags: inputType 101 (FC01 Coils)
  const doTags = useMemo(
    () => (currentScanner?.tags ?? []).filter((t: any) => t.inputType === 101 || t.input_type === 101),
    [currentScanner],
  );

  const getChannelTag = (channel: number, type: 'DI' | 'DO') => {
    const address = channel - 1; // Waveshare: CH1 = address 0
    const tags = type === 'DI' ? diTags : doTags;
    return tags.find((t: any) => t.address === address);
  };

  const getChannelValue = (channel: number, type: 'DI' | 'DO'): boolean => {
    const tag = getChannelTag(channel, type);
    if (!tag) return false;
    const live = liveReadings.find((r) => r.tag_name === tag.name);
    return live ? Boolean(live.value) : false;
  };

  const hasReading = (channel: number, type: 'DI' | 'DO') => {
    const tag = getChannelTag(channel, type);
    return !!tag && liveReadings.some((r) => r.tag_name === tag.name);
  };

  const lastChangeAt = (channel: number, type: 'DI' | 'DO') => {
    const tag = getChannelTag(channel, type);
    const hist = tag ? historyRef.current[tag.name] : undefined;
    // The first sample is the initial state, not a change.
    return hist && hist.length > 1 ? hist[hist.length - 1].t : undefined;
  };

  const diStates = CHANNELS.map((ch) => getChannelValue(ch, 'DI'));
  const doStates = CHANNELS.map((ch) => getChannelValue(ch, 'DO'));
  const activeInputs = diStates.filter(Boolean).length;
  const activeOutputs = doStates.filter(Boolean).length;

  // Clear a pending write once the device reports the requested state, or after a timeout.
  useEffect(() => {
    const entries = Object.entries(pendingTargets);
    if (entries.length === 0) return;
    const now = Date.now();
    let changed = false;
    const next = { ...pendingTargets };
    for (const [chKey, target] of entries) {
      const ch = Number(chKey);
      if (getChannelValue(ch, 'DO') === target.value) {
        delete next[ch];
        changed = true;
      } else if (now - target.at > CONFIRM_TIMEOUT_MS) {
        delete next[ch];
        changed = true;
        toast.warning(`DO${ch}: no confirmation from the device yet`, { description: 'The command was queued; check the bridge connection.' });
      }
    }
    if (changed) setPendingTargets(next);
  });

  // ──────────────────────────────────────────────────
  // Toggle a single DO coil via bridge
  // ──────────────────────────────────────────────────
  const toggleOutput = async (channel: number) => {
    if (!currentScanner || togglingChannel !== null) return;
    const address = channel - 1;
    const newValue = !getChannelValue(channel, 'DO');
    setTogglingChannel(channel);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/scanners/${currentScanner.id}/coil/${address}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ value: newValue }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? 'Failed to toggle output');
      }
      setPendingTargets((prev) => ({ ...prev, [channel]: { value: newValue, at: Date.now() } }));
      toast.success(`DO${channel} → ${newValue ? 'ON' : 'OFF'} sent`);
    } catch (err: any) {
      toast.error(err.message ?? 'Error toggling output');
    } finally {
      setTogglingChannel(null);
    }
  };

  const setAllOutputs = async (state: boolean) => {
    const targets = CHANNELS.filter((ch) => getChannelValue(ch, 'DO') !== state);
    if (targets.length === 0) return;
    setBulk({ state, done: 0, total: targets.length });
    let done = 0;
    for (const ch of targets) {
      await toggleOutput(ch);
      done += 1;
      setBulk({ state, done, total: targets.length });
      await new Promise((r) => setTimeout(r, 220));
    }
    setTimeout(() => setBulk(null), 800);
  };

  // ──────────────────────────────────────────────────
  // Auto-provision missing DI / DO tags
  // ──────────────────────────────────────────────────
  const provisionWaveshareTags = async () => {
    if (!currentScanner || provisioning) return;
    setProvisioning(true);
    try {
      for (const ch of CHANNELS) {
        const address = ch - 1;
        if (!diTags.find((t: any) => t.address === address)) {
          await fetch(`${API_BASE}/api/admin/scanners/${currentScanner.id}/tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: `DI${ch}`, address, inputType: 102 }),
          });
        }
        if (!doTags.find((t: any) => t.address === address)) {
          await fetch(`${API_BASE}/api/admin/scanners/${currentScanner.id}/tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: `DO${ch}`, address, inputType: 101 }),
          });
        }
      }
      toast.success('Waveshare tags provisioned successfully!');
      await loadScanners();
    } catch {
      toast.error('Failed to provision tags');
    } finally {
      setProvisioning(false);
    }
  };

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const now = Date.now();
  const fadeUp = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] as any },
  });

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <motion.section {...fadeUp(0)} className="rounded-xl border bg-card p-5">
        <div className="grid gap-5 lg:grid-cols-[1fr_minmax(0,1.1fr)] lg:items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Cable className="h-4 w-4 text-emerald-500" />
              Modbus RTU IO Module
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">
              Waveshare 8CH Command Center
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Live Waveshare Modbus RTU IO 8CH — 8 digital inputs (FC02) and 8 digital outputs (FC01/FC05).
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {/* Device picker */}
              <div className="w-56">
                <Select
                  value={selectedScannerId ?? undefined}
                  onValueChange={setSelectedScannerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Device" />
                  </SelectTrigger>
                  <SelectContent>
                    {scanners.length === 0 && (
                      <SelectItem value="__none__" disabled>
                        No Devices Configured
                      </SelectItem>
                    )}
                    {scanners.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Connection badge */}
              <motion.div
                layout
                className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 ${
                  connected
                    ? 'border-emerald-500/30 bg-emerald-500/10'
                    : 'border-amber-500/30 bg-amber-500/10'
                }`}
              >
                <span className="relative flex h-3 w-3">
                  {connected && (
                    <motion.span
                      key={lastPacketAt}
                      className="absolute inset-0 rounded-full bg-emerald-500"
                      initial={{ scale: 1, opacity: 0.7 }}
                      animate={{ scale: reduce ? 1 : 2.6, opacity: 0 }}
                      transition={{ duration: 1 }}
                    />
                  )}
                  <span className={`relative h-3 w-3 rounded-full ${connected ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.8)]' : 'bg-amber-500'}`} />
                </span>
                <div>
                  <p className="text-sm font-semibold">
                    {connected ? 'Connected' : 'Offline'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {currentScanner
                      ? `Slave ${currentScanner.slave_id ?? currentScanner.slaveId} / ${
                          currentScanner.baud_rate ?? currentScanner.baudRate
                        } baud`
                      : 'No device selected'}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          <ModuleHero
            di={diStates}
            doo={doStates}
            connected={connected}
            packetAt={connected ? lastPacketAt : undefined}
            name={currentScanner?.name ?? 'No device selected'}
          />
        </div>
      </motion.section>

      {/* ── Missing tags warning ── */}
      <AnimatePresence>
        {currentScanner && (diTags.length < 8 || doTags.length < 8) && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-amber-500">Missing Standard Tags</p>
                  <p className="text-sm text-muted-foreground">
                    {diTags.length} / 8 DI tags and {doTags.length} / 8 DO tags found. Click
                    "Provision" to automatically create all 16 standard Waveshare tags in the backend.
                  </p>
                </div>
                <Button
                  onClick={provisionWaveshareTags}
                  variant="outline"
                  disabled={provisioning}
                  className="flex-shrink-0 border-amber-500 text-amber-500 hover:bg-amber-500/10"
                >
                  {provisioning ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" />
                  )}
                  Provision Auto-Tags
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── KPI cards ── */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div {...fadeUp(1)}>
          <Card className="h-full overflow-hidden">
            <div className="h-0.5" style={{ backgroundColor: DI_COLOR }} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Digital Inputs</p>
                <Activity className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="mt-1 text-2xl font-bold"><CountUp value={activeInputs} /> <span className="text-base font-medium text-muted-foreground">/ 8 on</span></p>
              <SegmentBar states={diStates} color={DI_COLOR} />
            </CardContent>
          </Card>
        </motion.div>
        <motion.div {...fadeUp(2)}>
          <Card className="h-full overflow-hidden">
            <div className="h-0.5" style={{ backgroundColor: DO_COLOR }} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Digital Outputs</p>
                <Power className="h-5 w-5 text-cyan-500" />
              </div>
              <p className="mt-1 text-2xl font-bold"><CountUp value={activeOutputs} /> <span className="text-base font-medium text-muted-foreground">/ 8 on</span></p>
              <SegmentBar states={doStates} color={DO_COLOR} />
            </CardContent>
          </Card>
        </motion.div>
        <motion.div {...fadeUp(3)}>
          <Card className="h-full overflow-hidden">
            <div className="h-0.5 bg-violet-500" />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Poll Cycle</p>
                <RefreshCw className={`h-5 w-5 text-violet-500 ${connected ? 'animate-spin' : ''}`} style={{ animationDuration: `${Math.max(1, pollingIntervalMs / 1000)}s` }} />
              </div>
              <p className="mt-1 text-2xl font-bold tabular-nums">{pollingIntervalMs} <span className="text-base font-medium text-muted-foreground">ms</span></p>
              <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {lastPacketAt ? `Last update ${Math.max(0, Math.round((now - lastPacketAt) / 1000))}s ago` : 'Waiting for first update'}
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div {...fadeUp(4)}>
          <Card className={`h-full overflow-hidden ${connected ? '' : 'ring-1 ring-amber-500/40'}`}>
            <div className={`h-0.5 ${connected ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Safety State</p>
                {connected ? <ShieldAlert className="h-5 w-5 text-emerald-500" /> : <WifiOff className="h-5 w-5 text-amber-500" />}
              </div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={connected ? 'live' : 'off'}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className={`mt-1 text-2xl font-bold ${connected ? 'text-emerald-500' : 'text-amber-500'}`}
                >
                  {connected ? 'Live' : 'Offline'}
                </motion.p>
              </AnimatePresence>
              <p className="mt-3 text-xs text-muted-foreground">{connected ? 'Device answering the bridge' : 'No data from the bridge'}</p>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* ── DI + DO panels ── */}
      <div className="space-y-5">
        {/* Digital Inputs */}
        <motion.div {...fadeUp(5)}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">Digital Inputs</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  FC02 — Read-only discrete inputs
                </p>
              </div>
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-500">
                <CountUp value={activeInputs} /> ON
              </span>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {CHANNELS.map((ch, i) => {
                  const active = getChannelValue(ch, 'DI');
                  const known = hasReading(ch, 'DI');
                  return (
                    <motion.div
                      key={`di-${ch}`}
                      initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.15 + i * 0.03 }}
                      className={`relative overflow-hidden rounded-xl border p-4 transition-colors duration-300 ${
                        active ? 'border-emerald-500/50 bg-emerald-500/10' : 'bg-muted/20'
                      }`}
                      style={active ? { boxShadow: `inset 0 0 24px ${DI_COLOR}22, 0 0 18px -6px ${DI_COLOR}` } : undefined}
                    >
                      {/* edge ripple when the input turns on */}
                      <AnimatePresence>
                        {active && !reduce && (
                          <motion.span
                            key={`rip-${lastChangeAt(ch, 'DI') ?? 'init'}`}
                            className="pointer-events-none absolute right-4 top-4 h-3 w-3 rounded-full"
                            style={{ backgroundColor: DI_COLOR }}
                            initial={{ scale: 1, opacity: 0.6 }}
                            animate={{ scale: 14, opacity: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.9, ease: 'easeOut' }}
                          />
                        )}
                      </AnimatePresence>
                      <div className="relative flex items-center justify-between">
                        <span className="text-sm font-semibold">DI{ch}</span>
                        <Led on={active} color={DI_COLOR} size={12} label={`DI${ch} ${active ? 'on' : 'off'}`} />
                      </div>
                      <div className="relative mt-3 h-8 overflow-hidden">
                        <AnimatePresence mode="popLayout" initial={false}>
                          <motion.p
                            key={active ? 'on' : 'off'}
                            initial={{ y: 24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -24, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                            className={`text-2xl font-bold ${active ? 'text-emerald-500' : 'text-muted-foreground'}`}
                          >
                            {known ? (active ? 'ON' : 'OFF') : '—'}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                      <p className="relative mt-1 text-xs text-muted-foreground">
                        Input channel {ch} · {ago(lastChangeAt(ch, 'DI'))}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Digital Outputs */}
        <motion.div {...fadeUp(6)}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">Digital Outputs</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  FC01 / FC05 — Coil write (via bridge)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <AnimatePresence>
                  {bulk && (
                    <motion.span
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2 text-xs text-muted-foreground"
                    >
                      Switching {bulk.state ? 'on' : 'off'} {bulk.done}/{bulk.total}
                      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                        <motion.span className="block h-full rounded-full" style={{ backgroundColor: DO_COLOR }} animate={{ width: `${(bulk.done / bulk.total) * 100}%` }} />
                      </span>
                    </motion.span>
                  )}
                </AnimatePresence>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={togglingChannel !== null || !!bulk}
                  onClick={() => setAllOutputs(false)}
                >
                  All Off
                </Button>
                <Button
                  size="sm"
                  disabled={togglingChannel !== null || !!bulk}
                  onClick={() => setAllOutputs(true)}
                >
                  All On
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {CHANNELS.map((ch, i) => {
                  const active = getChannelValue(ch, 'DO');
                  const isToggling = togglingChannel === ch;
                  const pending = pendingTargets[ch];
                  const known = hasReading(ch, 'DO');
                  const statusText = isToggling ? 'Sending…' : pending ? `Confirming ${pending.value ? 'ON' : 'OFF'}…` : ago(lastChangeAt(ch, 'DO'));
                  return (
                    <motion.div
                      key={`do-${ch}`}
                      initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 + i * 0.03 }}
                      className={`relative overflow-hidden rounded-xl border p-4 transition-colors duration-300 ${
                        active ? 'border-cyan-500/50 bg-cyan-500/10' : 'bg-muted/20'
                      }`}
                      style={active ? { boxShadow: `inset 0 0 24px ${DO_COLOR}22, 0 0 18px -6px ${DO_COLOR}` } : undefined}
                    >
                      {/* shimmer while a write is in flight / unconfirmed */}
                      {(isToggling || pending) && !reduce && (
                        <motion.span
                          className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent"
                          initial={{ left: '-35%' }}
                          animate={{ left: '110%' }}
                          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                        />
                      )}
                      <div className="relative flex items-center justify-between">
                        <span className="text-sm font-semibold">DO{ch}</span>
                        <Led on={active} color={DO_COLOR} size={12} label={`DO${ch} ${active ? 'on' : 'off'}`} />
                      </div>
                      <div className="relative mt-3 flex items-center justify-between gap-3">
                        <div className="h-8 overflow-hidden">
                          <AnimatePresence mode="popLayout" initial={false}>
                            <motion.p
                              key={active ? 'on' : 'off'}
                              initial={{ y: 24, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={{ y: -24, opacity: 0 }}
                              transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                              className={`text-2xl font-bold ${active ? 'text-cyan-500' : 'text-muted-foreground'}`}
                            >
                              {known ? (active ? 'ON' : 'OFF') : '—'}
                            </motion.p>
                          </AnimatePresence>
                        </div>
                        <OutputSwitch
                          on={pending ? pending.value : active}
                          busy={isToggling || togglingChannel !== null || !!bulk}
                          onClick={() => toggleOutput(ch)}
                          label={`DO${ch} output, currently ${active ? 'on' : 'off'}`}
                        />
                      </div>
                      <p className={`relative mt-2 text-xs ${pending || isToggling ? 'font-medium text-cyan-500' : 'text-muted-foreground'}`}>
                        {statusText}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Signal timeline */}
        <motion.div {...fadeUp(7)}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-base"><Radio className="h-4 w-4 text-muted-foreground" /> Signal timeline</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">Last 60 seconds · filled = ON · ticks mark each switch</p>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DI_COLOR }} /> Inputs</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DO_COLOR }} /> Outputs</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-x-8 gap-y-1.5 lg:grid-cols-2">
                <div className="space-y-1.5">
                  {CHANNELS.map((ch) => {
                    const tag = getChannelTag(ch, 'DI');
                    return (
                      <TimelineRow key={`tl-di-${ch}`} label={`DI${ch}`} color={DI_COLOR} history={tag ? historyRef.current[tag.name] || [] : []} current={hasReading(ch, 'DI') ? getChannelValue(ch, 'DI') : null} now={now} />
                    );
                  })}
                  <TimelineAxis />
                </div>
                <div className="space-y-1.5">
                  {CHANNELS.map((ch) => {
                    const tag = getChannelTag(ch, 'DO');
                    return (
                      <TimelineRow key={`tl-do-${ch}`} label={`DO${ch}`} color={DO_COLOR} history={tag ? historyRef.current[tag.name] || [] : []} current={hasReading(ch, 'DO') ? getChannelValue(ch, 'DO') : null} now={now} />
                    );
                  })}
                  <TimelineAxis />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* ── Footer ── */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span>
            Live data is streamed directly from the edge bridge. DO writes are sent as Modbus FC05
            (Write Single Coil) commands through the bridge command queue.
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
